import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setSortBy, setSortDirection } from '../../../../store/reducers/dropbox';
import { toast } from 'react-toastify';
import * as dropboxService from '../../../../services/dropbox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolder,
  faFile,
  faFilePdf,
  faFileWord,
  faFileExcel,
  faFilePowerpoint,
  faFileImage,
  faFileVideo,
  faFileAudio,
  faFileArchive,
  faEllipsisH,
  faSort,
  faSortUp,
  faSortDown
} from '@fortawesome/free-solid-svg-icons';
import ConfirmMoveModal from '../Modals/ConfirmMoveModal';
import ConfirmMoveFileModal from '../Modals/ConfirmMoveFileModal';
import { formatDateTime } from '../../utils/dateUtils';
import { filenameIncludesExtension } from '../../utils/nameUtils';

interface FileListModernProps {
  folders: any[];
  files: any[];
  selectedIds?: string[]; // Add selectedIds prop for synchronization
  onFolderClick: (folderId: string) => void;
  onFileClick?: (file: any) => void;
  onContextMenu: (item: any, type: 'file' | 'folder', event: React.MouseEvent) => void;
  onSelectionChange?: (selectedIds: string[], selectedItems: any[]) => void;
  onItemMoved?: () => void;
  contextMenuItemId?: string | null; // Add prop to track which item has context menu open
  currentView?: 'all' | 'favorites' | 'recent' | 'trash'; // Add currentView prop
}

const FileListModern: React.FC<FileListModernProps> = ({
  folders,
  files,
  selectedIds: parentSelectedIds = [], // Receive selectedIds from parent
  onFolderClick,
  onFileClick,
  onContextMenu,
  onSelectionChange,
  onItemMoved,
  contextMenuItemId, // Add the new prop
  currentView = 'all' // Add currentView with default value
}) => {
  const dispatch = useDispatch();
  const { sortBy: globalSortBy, sortDirection: globalSortDirection } = useSelector((state: any) => state.dropbox);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const prevSelectedIdsRef = useRef<string>('');
  const prevParentSelectedIdsRef = useRef<string>('');

  // Synchronize local selectedIds with parent selectedIds
  // This ensures that when the parent clears selection (e.g., clicking the X button),
  // the local state is updated and checkboxes are visually unchecked
  useEffect(() => {
    const parentSelectedIdsString = parentSelectedIds.sort().join(',');
    
    // Only update if parent selection actually changed
    if (prevParentSelectedIdsRef.current !== parentSelectedIdsString) {
      prevParentSelectedIdsRef.current = parentSelectedIdsString;
      setSelectedIds(parentSelectedIds);
    }
  }, [parentSelectedIds]);
  const [draggedItem, setDraggedItem] = useState<{ id: string; type: 'file' | 'folder'; name: string } | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [showConfirmMove, setShowConfirmMove] = useState(false);
  const [moveData, setMoveData] = useState<{ sourceId: string; sourceName: string; targetId: string; targetName: string; type: 'file' | 'folder' } | null>(null);
  const [isMoving, setIsMoving] = useState(false);

  // Notify parent when selection changes (with proper memoization)
  useEffect(() => {
    const selectedIdsString = selectedIds.sort().join(',');
    
    // Only notify if selection actually changed
    if (prevSelectedIdsRef.current === selectedIdsString) {
      return;
    }
    prevSelectedIdsRef.current = selectedIdsString;

    if (onSelectionChange) {
      // Add type field to each item
      const foldersWithType = folders.map(f => ({ ...f, type: 'folder' as const }));
      const filesWithType = files.map(f => ({ ...f, type: 'file' as const }));
      const allItems = [...foldersWithType, ...filesWithType];
      const selected = allItems.filter(item => selectedIds.includes(item.id));
      onSelectionChange(selectedIds, selected);
    }
  }, [selectedIds, folders, files, onSelectionChange]);

  const getFileIcon = (file: any) => {
    const ext = file.extension?.toLowerCase();
    const mime = file.mime_type?.toLowerCase();

    if (mime?.includes('pdf')) return { icon: faFilePdf, className: 'pdf' };
    if (mime?.includes('word') || ext === 'doc' || ext === 'docx') return { icon: faFileWord, className: 'word' };
    if (mime?.includes('excel') || ext === 'xls' || ext === 'xlsx') return { icon: faFileExcel, className: 'excel' };
    if (mime?.includes('powerpoint') || ext === 'ppt' || ext === 'pptx') return { icon: faFilePowerpoint, className: 'ppt' };
    if (mime?.includes('image')) return { icon: faFileImage, className: 'image' };
    if (mime?.includes('video')) return { icon: faFileVideo, className: 'video' };
    if (mime?.includes('audio')) return { icon: faFileAudio, className: 'audio' };
    if (mime?.includes('zip') || mime?.includes('rar') || mime?.includes('7z')) return { icon: faFileArchive, className: 'archive' };

    return { icon: faFile, className: 'default' };
  };

  // Use the utility function for consistent date formatting
  const formatDate = formatDateTime;

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '--';
    const mb = bytes / 1024 / 1024;
    if (mb < 1) return `${(bytes / 1024).toFixed(0)} KB`;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    return `${(mb / 1024).toFixed(1)} GB`;
  };

  // Handle double click to open folder/file
  const handleDoubleClick = (item: any, type: 'file' | 'folder') => {
    if (type === 'folder') {
      onFolderClick(item.id);
    } else if (onFileClick) {
      onFileClick(item);
    }
  };

  // Handle checkbox change for individual items
  const handleCheckboxChange = (id: string, checked: boolean, e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (checked) {
      const newSelected = [...selectedIds, id];
      setSelectedIds(newSelected);
    } else {
      const newSelected = selectedIds.filter(itemId => itemId !== id);
      setSelectedIds(newSelected);
    }
  };

  // Handle master checkbox (select all / deselect all)
  const handleMasterCheckbox = (checked: boolean, e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (checked) {
      const allIds = [...folders.map(f => f.id), ...files.map(f => f.id)];
      setSelectedIds(allIds);
    } else {
      setSelectedIds([]);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (item: any, type: 'file' | 'folder', e: React.DragEvent) => {
    setDraggedItem({ id: item.id, type, name: item.name });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('itemId', item.id);
    e.dataTransfer.setData('itemType', type);
    e.dataTransfer.setData('itemName', item.name);
  };

  const handleDragEnd = () => {
    // Reset drag state when drag ends (cancelled or completed)
    setDraggedItem(null);
    setDropTarget(null);
  };

  const handleDragOver = (folderId: string, e: React.DragEvent) => {
    e.preventDefault();
    if (draggedItem && draggedItem.id !== folderId) {
      e.dataTransfer.dropEffect = 'move';
      setDropTarget(folderId);
    }
  };

  const handleDragLeave = () => {
    setDropTarget(null);
  };

  const handleDrop = async (targetFolder: any, e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const itemId = e.dataTransfer.getData('itemId');
    const itemType = e.dataTransfer.getData('itemType') as 'file' | 'folder';
    const itemName = e.dataTransfer.getData('itemName');
    
    if (!itemId || itemId === targetFolder.id) {
      setDraggedItem(null);
      setDropTarget(null);
      return;
    }

    // Show confirmation modal
    setMoveData({
      sourceId: itemId,
      sourceName: itemName,
      targetId: targetFolder.id,
      targetName: targetFolder.name,
      type: itemType
    });
    setShowConfirmMove(true);
    setDraggedItem(null);
    setDropTarget(null);
  };

  const handleConfirmMove = async () => {
    if (!moveData) return;

    setIsMoving(true);
    try {
      if (moveData.type === 'file') {
        // Move file to target folder
        const response = await dropboxService.moveFile(moveData.sourceId, moveData.targetId);
        if (response?.success) {
          toast.success(`File moved to ${moveData.targetName}`);
          if (onItemMoved) onItemMoved();
        } else {
          toast.error('Failed to move file');
        }
      } else if (moveData.type === 'folder') {
        // Move folder to target folder
        const response = await dropboxService.moveFolder(moveData.sourceId, moveData.targetId);
        if (response?.success) {
          toast.success(`Folder moved to ${moveData.targetName}`);
          if (onItemMoved) onItemMoved();
        } else {
          toast.error('Failed to move folder');
        }
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to move item');
    } finally {
      setIsMoving(false);
      setShowConfirmMove(false);
      setMoveData(null);
    }
  };

  const handleCancelMove = () => {
    setShowConfirmMove(false);
    setMoveData(null);
  };

  // Check if all items are selected
  const totalItems = folders.length + files.length;
  const allSelected = totalItems > 0 && selectedIds.length === totalItems;

  // Handle table column sorting (updates global Redux state)
  const handleTableSort = (column: 'name' | 'date' | 'size') => {
    // Disable sorting for Recent view
    if (currentView === 'recent') return;
    
    if (globalSortBy === column) {
      dispatch(setSortDirection(globalSortDirection === 'asc' ? 'desc' : 'asc'));
    } else {
      dispatch(setSortBy(column));
      dispatch(setSortDirection('asc'));
    }
  };

  // Sort folders and files based on GLOBAL Redux sorting state
  // EXCEPT for Recent view, which maintains its own order based on accessed_at
  const sortedItems = useMemo(() => {
    const allItems = [
      ...folders.map(f => ({ ...f, type: 'folder' as const })),
      ...files.map(f => ({ ...f, type: 'file' as const }))
    ];

    // For Recent view, preserve the order from getFilteredFiles (sorted by accessed_at)
    if (currentView === 'recent') {
      return allItems;
    }

    // For other views, apply global sorting
    return allItems.sort((a, b) => {
      let comparison = 0;

      // Use GLOBAL sort column from Redux
      switch (globalSortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'date':
          const aDate = new Date(a.updated_at || 0).getTime();
          const bDate = new Date(b.updated_at || 0).getTime();
          comparison = aDate - bDate;
          break;
        case 'size':
          // Only files have size, folders are treated as 0
          const aSize = a.type === 'file' ? (a.size_bytes || 0) : 0;
          const bSize = b.type === 'file' ? (b.size_bytes || 0) : 0;
          comparison = aSize - bSize;
          break;
        case 'custom':
          // Sort by sort_order field
          const orderA = a.sort_order ?? 0;
          const orderB = b.sort_order ?? 0;
          comparison = orderA - orderB;
          break;
        default:
          comparison = a.name.localeCompare(b.name);
      }

      return globalSortDirection === 'asc' ? comparison : -comparison;
    });
  }, [folders, files, globalSortBy, globalSortDirection, currentView]);

  // Get sort icon for table columns based on global state
  const getTableSortIcon = (column: 'name' | 'date' | 'size') => {
    if (globalSortBy !== column) return faSort;
    return globalSortDirection === 'asc' ? faSortUp : faSortDown;
  };

  // Build folder path
  const buildFolderPath = (folderId: string | null) => {
    if (!folderId) return '/';
    const path: string[] = [];
    let currentId = folderId;
    while (currentId) {
      const folder = folders.find((f: any) => f.id === currentId);
      if (folder) {
        path.unshift(folder.name);
        currentId = folder.parent_id;
      } else {
        break;
      }
    }
    return path.length > 0 ? '/' + path.join('/') : '/';
  };

  return (
    <>
    <table className="dropbox-table-modern">
      <thead>
        <tr>
          <th className="checkbox-col">
            <input 
              type="checkbox" 
              checked={allSelected}
              onChange={(e) => handleMasterCheckbox(e.target.checked, e)}
            />
          </th>
          <th 
            onClick={() => handleTableSort('name')} 
            style={{ 
              cursor: currentView === 'recent' ? 'default' : 'pointer', 
              userSelect: 'none',
              opacity: currentView === 'recent' ? 0.7 : 1
            }}
          >
            Name {currentView !== 'recent' && <FontAwesomeIcon icon={getTableSortIcon('name')} style={{ marginLeft: '0.5rem', fontSize: '0.75rem', opacity: 0.6 }} />}
          </th>
          <th 
            className="size-col" 
            onClick={() => handleTableSort('size')} 
            style={{ 
              cursor: currentView === 'recent' ? 'default' : 'pointer', 
              userSelect: 'none',
              opacity: currentView === 'recent' ? 0.7 : 1
            }}
          >
            Size {currentView !== 'recent' && <FontAwesomeIcon icon={getTableSortIcon('size')} style={{ marginLeft: '0.5rem', fontSize: '0.75rem', opacity: 0.6 }} />}
          </th>
          {currentView === 'recent' ? (
            <>
              <th className="action-col">Action</th>
              <th className="accessed-col">Accessed At</th>
            </>
          ) : (
            <th className="modified-col" onClick={() => handleTableSort('date')} style={{ cursor: 'pointer', userSelect: 'none' }}>
              Last modified <FontAwesomeIcon icon={getTableSortIcon('date')} style={{ marginLeft: '0.5rem', fontSize: '0.75rem', opacity: 0.6 }} />
            </th>
          )}
          <th className="actions-col">Actions</th>
        </tr>
      </thead>
      <tbody>
        {sortedItems.map((item) => {
          const isFolder = item.type === 'folder';
          const fileIcon = !isFolder ? getFileIcon(item) : null;
          
          return (
            <tr
              key={`${item.type}-${item.id}`}
              className={`${selectedIds.includes(item.id) ? 'selected' : ''} ${draggedItem?.id === item.id ? 'dragging' : ''} ${dropTarget === item.id && isFolder ? 'drop-target' : ''} ${contextMenuItemId === item.id ? 'context-menu-active' : ''}`}
              draggable
              onDragStart={(e) => handleDragStart(item, item.type, e)}
              onDragEnd={handleDragEnd}
              {...(isFolder && {
                onDragOver: (e) => handleDragOver(item.id, e),
                onDragLeave: handleDragLeave,
                onDrop: (e) => handleDrop(item, e)
              })}
              onDoubleClick={(e) => {
                const target = e.target as HTMLElement;
                if (target.closest('.checkbox-col')) {
                  return;
                }
                handleDoubleClick(item, item.type);
              }}
              onContextMenu={(e) => onContextMenu(item, item.type, e)}
            >
              <td className="checkbox-col" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selectedIds.includes(item.id)}
                  onChange={(e) => handleCheckboxChange(item.id, e.target.checked, e)}
                  onClick={(e) => e.stopPropagation()}
                />
              </td>
              <td>
                <div className="name-col">
                  {isFolder ? (
                    <div className="dropbox-file-icon dropbox-folder-icon">
                      <FontAwesomeIcon icon={faFolder} />
                    </div>
                  ) : (
                    <div className={`dropbox-file-icon ${fileIcon?.className}`}>
                      <FontAwesomeIcon icon={fileIcon?.icon || faFile} />
                    </div>
                  )}
                  <span className="dropbox-file-name" title={`${item.name}${!isFolder && item.extension && !filenameIncludesExtension(item.name, item.extension) ? `.${item.extension}` : ''}`}>
                    {item.name}
                    {!isFolder && item.extension && !filenameIncludesExtension(item.name, item.extension) && (
                      <span className="dropbox-file-extension">.{item.extension}</span>
                    )}
                  </span>
                </div>
              </td>
              <td className="size-col">
                {!isFolder ? formatFileSize(item.size_bytes) : '--'}
              </td>
              {currentView === 'recent' ? (
                <>
                  <td className="action-col">
                    <span className="badge badge-info" style={{ textTransform: 'capitalize' }}>
                      {item.action || 'viewed'}
                    </span>
                  </td>
                  <td className="accessed-col">{formatDateTime(item.accessed_at)}</td>
                </>
              ) : (
                <td className="modified-col">{formatDate(item.updated_at)}</td>
              )}
              <td className="actions-col">
                <div className="dropbox-row-actions">
                  <button
                    className="dropbox-action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onContextMenu(item, item.type, e);
                    }}
                  >
                    <FontAwesomeIcon icon={faEllipsisH} />
                  </button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    
    {/* Confirmation Modal */}
    {showConfirmMove && moveData && moveData.type === 'folder' && (
      <ConfirmMoveModal
        sourceFolderName={moveData.sourceName}
        targetFolderName={moveData.targetName}
        sourceFolderId={moveData.sourceId}
        targetFolderId={moveData.targetId}
        onConfirm={handleConfirmMove}
        onCancel={handleCancelMove}
        isLoading={isMoving}
      />
    )}
    
    {/* Simple confirmation for files */}
    {showConfirmMove && moveData && moveData.type === 'file' && (
      <ConfirmMoveFileModal
        fileName={moveData.sourceName}
        currentPath={(() => {
          // Build current folder path
          const currentFolder = folders.find((f: any) => f.id === files.find((file: any) => file.id === moveData.sourceId)?.folder_id);
          if (!currentFolder) return '/';
          const path: string[] = [];
          let currentId = currentFolder.id;
          while (currentId) {
            const folder = folders.find((f: any) => f.id === currentId);
            if (folder) {
              path.unshift(folder.name);
              currentId = folder.parent_id;
            } else {
              break;
            }
          }
          return path.length > 0 ? '/' + path.join('/') : '/';
        })()}
        targetPath={(() => {
          // Build target folder path
          const path: string[] = [];
          let currentId = moveData.targetId;
          while (currentId) {
            const folder = folders.find((f: any) => f.id === currentId);
            if (folder) {
              path.unshift(folder.name);
              currentId = folder.parent_id;
            } else {
              break;
            }
          }
          return path.length > 0 ? '/' + path.join('/') : '/';
        })()}
        onConfirm={handleConfirmMove}
        onCancel={handleCancelMove}
        isLoading={isMoving}
      />
    )}
  </>
  );
};

export default FileListModern;
